// 쿠폰함 화면.
// 작업계획서 4.6: 마이페이지에서 쿠폰 확인. 사용 가능 쿠폰을 위에, 사용/만료된 쿠폰을 아래에 보여준다.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  COUPON_STATUS,
  formatCouponBenefit,
  formatCouponConditions,
  type CouponDto,
} from "@oo/shared";
import { getCoupons } from "@/features/coupons/couponsApi.ts";

// 내 쿠폰함 화면.
export default function CouponsPage() {
  const couponsQuery = useQuery({ queryKey: ["coupons"], queryFn: getCoupons });
  const coupons = couponsQuery.data ?? [];
  const usable = coupons.filter((c) => c.usable);
  const others = coupons.filter((c) => !c.usable);

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-28">
      <h1 className="text-2xl font-bold">쿠폰함</h1>

      {couponsQuery.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 p-10 text-center text-sm text-ink-600">
          보유한 쿠폰이 없습니다.
        </div>
      ) : (
        <>
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold text-ink-700">사용 가능 ({usable.length})</h2>
            {usable.length === 0 ? (
              <p className="text-sm text-ink-600">사용 가능한 쿠폰이 없습니다.</p>
            ) : (
              usable.map((c) => <CouponCard key={c.id} coupon={c} />)
            )}
          </section>
          {others.length > 0 ? (
            <section className="flex flex-col gap-2">
              {/* 사용 완료(used) 쿠폰은 서버에서 제외되므로, 여기엔 '사용 중'(주문 적용 중)과 '기간 만료'만 남는다. */}
              <h2 className="text-sm font-semibold text-ink-600">사용 중/만료</h2>
              {others.map((c) => (
                <CouponCard key={c.id} coupon={c} dimmed />
              ))}
            </section>
          ) : null}
        </>
      )}

      <Link to="/me" className="text-center text-sm text-ink-600 underline">
        마이페이지로
      </Link>
    </main>
  );
}

function CouponCard({ coupon, dimmed }: { coupon: CouponDto; dimmed?: boolean }) {
  const expireLabel = coupon.expiresAt.slice(0, 10).replace(/-/gu, ".");
  // 사용 조건(최소 주문금액/최대 할인) 문구.
  const conditions = formatCouponConditions(coupon);
  const statusLabel =
    coupon.status === COUPON_STATUS.RESERVED
      ? "사용 중"
      : coupon.status === COUPON_STATUS.USED
        ? "사용 완료"
        : !coupon.usable
          ? "기간 만료"
          : null;
  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-4 ${
        dimmed ? "border-ink-200 bg-ink-50 opacity-70" : "border-brand-600 bg-white"
      }`}
    >
      <div className="min-w-0">
        <p className="text-base font-bold">{formatCouponBenefit(coupon.type, coupon.value)}</p>
        <p className="mt-0.5 truncate text-sm text-ink-600">{coupon.name}</p>
        {conditions.length > 0 ? (
          <p className="mt-0.5 text-xs text-ink-600">{conditions.join(" · ")}</p>
        ) : null}
        <p className="mt-0.5 text-xs text-ink-600">~ {expireLabel} 까지</p>
      </div>
      {statusLabel ? (
        <span className="shrink-0 rounded-full bg-ink-200 px-2.5 py-0.5 text-xs text-ink-600">
          {statusLabel}
        </span>
      ) : null}
    </div>
  );
}
